<template>
  <section class="pdp-primary">
    <header :class="headerClass">
      <p>{{ product.narrative_tag_line }}</p>
    </header>
    <slot name="subheader"></slot>
    <div id="pdp-primary-product" class="pdp-primary-product">
      <div class="image columns medium-6">
        <picture>
          <source :srcset="product.bottle_image_optimized" type="image/webp" />
          <source :srcset="product.bottle_image_url" type="image/jpeg" />
          <!-- No alt label for image since label (title) is already present above for screen readers -->
          <img :src="product.bottle_image_url" alt="" data-not-lazy />
        </picture>
        <div v-if="!product.is_sampler" class="pdp-review-scores">
          <ul>
            <li
              v-for="r in product.expert_reviews"
              v-show="!r.hide_points"
              :key="r.id"
            >
              <div v-if="!simple" class="show-for-large-up">
                <strong
                  ><span v-if="r.min_points">{{ r.min_points }} -</span>
                  {{ r.points }}<span v-if="r.show_plus">+</span> pts</strong
                >
                {{ r.expert.full_name }}
              </div>
              <div :class="{ 'show-for-medium-down': !simple }">
                <strong
                  ><span v-if="r.min_points">{{ r.min_points }} -</span>
                  {{ r.points }}<span v-if="r.show_plus">+</span> pts</strong
                >
                {{ abbreviateExpert(r.expert) }}
              </div>
            </li>
          </ul>
        </div>
        <div v-if="!simple" class="values show-for-medium-up">
          <ul class="check-list">
            <li>Curated by unrivaled experts</li>
            <li>Choose your delivery date</li>
            <li>Temperature controlled shipping options</li>
            <li>Get credited back if a wine fails to impress</li>
          </ul>
        </div>
      </div>
      <div class="details columns medium-6">
        <h1>{{ product.title }} {{ pdpSizeString }}</h1>
        <ProductInterlink :object="product" :text-only="simple" />
        <div
          v-if="!available && product.show_notify_me"
          class="limited-time-badge"
        >
          Sold Out
        </div>
        <div
          v-else-if="available && product.is_offer && !isFutures"
          class="limited-time-badge"
        >
          Limited Time Offer
        </div>
        <div
          v-if="available && firstAvailable && !isFutures"
          class="limited-time-badge"
        >
          {{ firstAvailable }}
        </div>
        <PricingTiers
          :object="product"
          :stock-record="product.default_record"
          :allow-add-to-cart="!simple"
          @error="setErrors"
        />
        <ErrorList v-if="errors" :errors="errors" />
        <div class="pricing-notes">
          <div
            v-if="!available && product.show_notify_me"
            class="shipping-note show-for-large-up"
          >
            Never miss out again: Sign up to receive notifications the instant
            wines from this producer go live!
          </div>
          <div>
            <span v-if="available && !simple">
              <strong>
                Shipping included<span
                  v-if="
                    !product.free_ground_shipping &&
                    (product.default_record === null ||
                      product.default_record.price_excl_tax <
                        metadata.settingsVars.STORE_FREE_SHIPPING_DOLLARS)
                  "
                >
                  on orders ${{
                    metadata.settingsVars.STORE_FREE_SHIPPING_DOLLARS
                  }}+</span
                >.
              </strong>
            </span>
            <span v-if="!product.is_discountable">
              This product is not eligible for discounts and promotions.
            </span>
          </div>
        </div>
        <div
          v-if="product.bottles_left > 0 && product.bottles_left <= 99"
          class="in-cart"
          :class="{ low: product.bottles_left < 25 }"
        >
          <div class="in-cart-message in">{{ bottlesLeft }}</div>
        </div>
        <div
          v-else-if="isCartCountAboveMinimum && !product.is_offer"
          class="in-cart"
        >
          <div class="in-cart-message in">
            {{ cartCount }}
            people have this in their cart.
          </div>
        </div>
      </div>
      <div
        class="details columns"
        :class="{ 'medium-6': !simple, 'small-12': simple }"
      >
        <Accordion v-slot="scope" :multiple="true">
          <AccordionItem
            title="The Story"
            :expanded-by-default="true"
            @expanded="scope.expanded"
          >
            <p v-html="product.content_teaser" />
            <p>
              <a
                v-scroll-to="{
                  el: '#pdp-story',
                  container: scrollId ? `#${scrollId}` : 'html, body',
                  offset: scrollId ? -20 : 0,
                }"
                class="read-all button-link"
                href="#pdp-story"
              >
                Read the full story
              </a>
            </p>
          </AccordionItem>
          <AccordionItem
            v-if="product.wine_video_url"
            title="Video"
            @expanded="scope.expanded"
          >
            <iframe
              data-not-lazy
              :src="product.wine_video_url"
              frameborder="0"
              width="100%"
              height="350px"
              allow="fullscreen"
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen
            />
          </AccordionItem>
          <AccordionItem
            v-if="!product.is_sampler"
            title="Wine Facts"
            @expanded="scope.expanded"
          >
            <ul class="pdp-flavors">
              <li
                v-for="flavor in flavors"
                :key="flavor.name"
                :class="flavor.class"
              >
                {{ flavor.name }}
              </li>
            </ul>
            <ul class="pdp-primary-stats">
              <li
                v-if="product.alcohol_percent && product.alcohol_percent > 0"
                class="abv"
              >
                ABV {{ Number(product.alcohol_percent).toFixed(2) }}%
              </li>
              <li class="decanting">
                {{ product.decanting_time || 'Enjoy right away' }}
              </li>
              <li v-if="product.drinking_window_summary" class="drinking">
                {{ product.drinking_window_summary }}
              </li>
              <li class="varietal">
                <div
                  v-for="varietal in product.varietals_ordered"
                  :key="varietal.id"
                >
                  <span v-show="varietal.amount_content_percentage"
                    >{{ varietal.amount_content_percentage }}%</span
                  >
                  {{ varietal.name }}
                </div>
              </li>
              <li v-if="product.serving_temp" class="temperature">
                Serving temperature - {{ product.serving_temp }}&deg;
              </li>
              <li v-if="product.closure" class="closure">
                {{ product.closure }}
              </li>
            </ul>
          </AccordionItem>
          <AccordionItem
            v-if="product.tasting_notes && product.tasting_notes.length > 0"
            title="Wine Access Tasting Note"
            @expanded="scope.expanded"
          >
            <div
              v-for="tasting_note in product.tasting_notes"
              :key="tasting_note.id"
              v-html="tasting_note.review"
            />
          </AccordionItem>
          <AccordionItem
            v-if="
              product.food_pairings_dict &&
              product.food_pairings_dict.length > 0
            "
            title="Food Pairings"
            @expanded="scope.expanded"
          >
            <ul class="pdp-foodpairing" aria-label="Food Pairings">
              <li
                v-for="food in product.food_pairings_dict"
                :key="food.id"
                :class="foodPairingClasses[food.name]"
              >
                {{ food.name }}
              </li>
            </ul>
          </AccordionItem>
          <AccordionItem
            v-if="product.expert_reviews && product.expert_reviews.length"
            title="Critic Reviews"
            @expanded="scope.expanded"
          >
            <div
              v-for="expert_review in product.expert_reviews"
              :key="expert_review.id"
              class="critic-reviews"
            >
              <h2 v-if="!expert_review.hide_points">
                <span v-if="expert_review.min_points"
                  >{{ expert_review.min_points }} -</span
                >
                {{ expert_review.points
                }}{{ expert_review.show_plus ? '+' : '' }} pts
              </h2>
              <h3>{{ expert_review.expert.full_name }}</h3>
              <div v-html="expert_review.review"></div>
            </div>
          </AccordionItem>
          <AccordionItem
            v-if="product.rating"
            title="Member Reviews"
            @expanded="scope.expanded"
          >
            <ProductMemberReviews
              :product="product"
              :date-format="dateFormat"
            />
          </AccordionItem>
        </Accordion>
      </div>

      <div v-if="!simple" class="values show-for-small-down">
        <ul class="check-list">
          <li>Curated by unrivaled experts</li>
          <li>Choose your delivery date</li>
          <li>Temperature controlled shipping options</li>
          <li>Get credited back if a wine fails to impress</li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
import { isAvailable } from '#common/utils/catalog'
import wineaccessLogoRoundBlack from '~/assets/images/wineaccess-logo-round-black.svg'
export default {
  components: {
    ErrorList: defineAsyncComponent(
      () => import('#common/components/ErrorList.vue'),
    ),
    ProductInterlink: defineAsyncComponent(
      () => import('@/components/Product/PDP/ProductInterlink'),
    ),
    PricingTiers: defineAsyncComponent(
      () => import('@/components/Product/PDP/PricingTiers'),
    ),
    ProductMemberReviews: defineAsyncComponent(
      () => import('@/components/Product/PDP/ProductMemberReviews'),
    ),
    Accordion: defineAsyncComponent(
      () => import('#common/components/Accordion/Accordion'),
    ),
    AccordionItem: defineAsyncComponent(
      () => import('#common/components/Accordion/AccordionItem'),
    ),
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    simple: {
      type: Boolean,
      default: false,
    },
    scrollId: {
      type: String,
      default: null,
    },
    isFutures: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errors: [],
      vimeo_thumbnail: null,
      productScales: {
        F: 'Fruit Intensity',
        O: 'Oak Intensity',
        B: 'Body',
        AL: 'Alcohol',
        D: 'Dryness',
        S: 'Sweetness',
        AC: 'Acidity',
        T: 'Tannin',
        AR: 'Aromatic Intensity',
      },
      foodPairingClasses: {
        Dessert: 'dessert',
        'Cured Meat': 'cured-meat',
        Spicy: 'spicy',
        Fish: 'fish',
        'Hard Cheese': 'hard-cheese',
        'Red Meat': 'red-meat',
        Shellfish: 'shellfish',
        'Soft Cheese': 'soft-cheese',
        Vegetables: 'vegetables',
        'White Meat': 'white-meat',
      },
    }
  },
  async fetch() {
    if (
      this.product.wine_video_url &&
      this.product.wine_video_url.includes('vimeo')
    ) {
      try {
        const vimeoObject = await this.$axios.get(
          `https://vimeo.com/api/v2/video/${this.product.wine_video_url
            .split('/')
            .pop()}.json`,
          { timeout: 500 },
        )
        this.vimeo_thumbnail = vimeoObject.data[0].thumbnail_large.split('_')[0]
      } catch {
        this.vimeo_thumbnail = wineaccessLogoRoundBlack
      }
    }
  },
  computed: {
    firstAvailable() {
      const firstShip =
        this.$auth.user.partner_id === 22
          ? this.product.first_ship_date_ca
          : this.product.first_ship_date_ny
      return this.$moment().isBefore(firstShip)
        ? `Ships ${this.$moment(firstShip).format('MM/DD')}`
        : undefined
    },
    headerClass() {
      return {
        'section-header': true,
        'red-wine': this.product.style_name === 'Red Wine',
        'sweet-wine': this.product.style_name === 'Sweet Wine',
        'white-wine': this.product.style_name === 'White Wine',
        sake: this.product.style_name === 'Sake',
        sampler: this.product.is_sampler,
        sparkling: this.product.style_name === 'Sparkling',
        rose: this.product.style_name === 'Rose',
        fortified: this.product.style_name === 'Fortified',
      }
    },
    isCartCountAboveMinimum() {
      return this.product.cart_count > 1
    },
    bottlesLeft() {
      if (this.product.bottles_left > 99) {
        return '99+ left'
      } else {
        return `${this.product.bottles_left} left`
      }
    },
    cartCount() {
      return this.product.cart_count
    },
    available() {
      return isAvailable(
        this.$auth.user,
        this.product,
        null,
        this.$config.public.validSiteIds,
      )
    },
    flavors() {
      const flavorsMap = {
        small: 'flavor-0',
        medium: 'flavor-1',
        large: 'flavor-2',
        full: 'flavor-3',
      }
      return this.product.style_scales.map((flavor) => {
        return {
          class: flavorsMap[flavor.value_name],
          name: this.productScales[flavor.scale_type],
        }
      })
    },
    dateFormat() {
      if (this.simple) {
        return 'MM/DD/YYYY'
      }
      return 'MMMM D, YYYY'
    },
    pdpSizeString() {
      if (this.product.size === 3000) {
        return 'Double Magnum'
      } else if (this.product.size === 1500) {
        return 'Magnum'
      } else if (this.product.size === 375) {
        return 'Half-Bottle'
      } else if (this.product.size) {
        return `${this.product.size} ml`
      }
      return ''
    },
  },
  created() {
    this.metadata = useMetadata()
  },
  methods: {
    setErrors(errors) {
      this.errors = errors
    },
    abbreviateExpert(expert) {
      return expert.full_name.length < 12 ? expert.full_name : expert.short_name
    },
  },
}
</script>
